import React from 'react';
import { researchStatusLabel } from '../utils/researchStatus';
import { currentLocale, targetingLabel } from '../i18n';
import { useTranslation } from 'react-i18next';
import ResearchAIAnalysisPanel from './ResearchAIAnalysisPanel';
import { getBranding } from '../config/branding';
import {
  ResearchData, DonutCard, Donut, HorizontalBarChart, StackedBarChart,
  ANSWER_COLORS, AGE_COLORS, GENDER_COLORS, GEO_COLORS, imgUrl, fmtDate, fmtDotted,
} from './ResearchDievoCharts';
import '../styles/research-share-dievo.css';

export type { ResearchData } from './ResearchDievoCharts';

/* Presentational Dievo-edition Share view. The parent (ResearchSharePage) does
   the fetch + loading/error and renders this ONLY when edition === 'dievo'.
   All styles are scoped under .research-share-v2, so Sense is never touched. */

const ResearchShareDievo: React.FC<{ data: ResearchData; token: string }> = ({ data, token }) => {
  const { t } = useTranslation();
  const progressPercentage = data.respondent_count > 0
    ? Math.min(100, (data.current_respondents / data.respondent_count) * 100) : 0;

  const brandName = getBranding().brand_name || 'Dievo';
  const startStr = data.progress?.original_launched_at;
  let targetCloseStr = '';
  if (startStr && data.progress) {
    const start = new Date(startStr);
    const avgDays = (data.progress.duration_days_min + data.progress.duration_days_max) / 2;
    targetCloseStr = fmtDate(new Date(start.getTime() + avgDays * 24 * 60 * 60 * 1000).toISOString());
  }

  return (
    <div className="research-share-v2">
      <div className="rsd-share-page">

        <header className="rsd-share-header">
          <div className="rsd-share-logo">
            <span className="rsd-brand-mark">{brandName.toLowerCase()}</span>
            <span className="rsd-share-divider" />
            <span className="rsd-share-kicker">ONE · Research</span>
          </div>
          <div className="rsd-share-meta">{t('research.generated')} {fmtDotted(new Date())}</div>
        </header>

        <div className="rsd-dashboard-header">
          <div className="rsd-dashboard-header-left">
            <h2 className="rsd-dashboard-title">{data.name}</h2>
            <span className={`rsd-dashboard-status rsd-status-${data.status.toLowerCase()}`}>{researchStatusLabel(data.status)}</span>
          </div>
          <div className="rsd-dashboard-header-right">
            <ResearchAIAnalysisPanel researchStatus={data.status} getUrl={`/api/sense/research/share/${token}/ai-analysis`} />
          </div>
        </div>

        <div className="rsd-dashboard-progress-section">
          <div className="rsd-progress-info">
            <div className="rsd-progress-numbers">
              <span className="rsd-progress-current">{data.current_respondents.toLocaleString(currentLocale())}</span>
              <span className="rsd-progress-separator">/</span>
              <span className="rsd-progress-target">{data.respondent_count.toLocaleString(currentLocale())}</span>
              <span className="rsd-progress-label">{t('research.respondentsLabel')}</span>
            </div>
            <span className="rsd-progress-percentage">{progressPercentage.toFixed(1)}%</span>
          </div>
          <div className="rsd-progress-bar-container">
            <div className={`rsd-progress-bar-fill${progressPercentage < 100 ? ' rsd-progress-bar-fill--live' : ''}`} style={{ width: `${progressPercentage}%` }} />
          </div>
          <div className="rsd-progress-dates">
            {startStr && (
              <div className="rsd-progress-date-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                <span>{t('research.started')} · <strong>{fmtDate(startStr)}</strong></span>
              </div>
            )}
            {targetCloseStr && (
              <div className="rsd-progress-date-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>
                <span>{data.progress?.is_complete ? t('research.statusCompleted') : t('research.targetClose')} · <strong>{targetCloseStr}</strong></span>
              </div>
            )}
            <div className="rsd-progress-date-item">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" /></svg>
              <span>{t('research.questionsCount', { count: data.questions.length })}</span>
            </div>
          </div>
        </div>

        <section className="rsd-dashboard-section">
          <h3 className="rsd-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
            {t('research.passport')}
          </h3>
          <div className="rsd-demographics-grid">
            <DonutCard title={t('research.ageDistribution')} data={(data.demographics.age || []).map((item, i) => ({ label: item.range, value: item.count, color: AGE_COLORS[i % AGE_COLORS.length] }))} />
            <DonutCard title={t('research.genderDistribution')} data={(data.demographics.gender || []).map((item, i) => ({ label: targetingLabel(item.label), value: item.count, color: GENDER_COLORS[i % GENDER_COLORS.length] }))} />
            <DonutCard title={t('research.geography')} maxLegendItems={5} data={(data.demographics.geo || []).map((item, i) => ({ label: item.region, value: item.count, color: GEO_COLORS[i % GEO_COLORS.length] }))} />
          </div>
        </section>

        <section className="rsd-dashboard-section">
          <h3 className="rsd-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><circle cx="12" cy="12" r="10" /><circle cx="12" cy="17" r="0.5" fill="currentColor" /></svg>
            {t('research.questionResults')}
          </h3>
          <div className="rsd-questions-results">
            {data.questions.map((question) => {
              const isMulti = question.question_type === 'multiple_choice';
              const isCreative = question.question_type === 'creative_test';
              return (
                <article key={question.id} className="rsd-question-result-card">
                  <header className="rsd-question-result-header">
                    <div className="rsd-question-number">Q{question.position}</div>
                    <div className="rsd-question-text">{question.question_text}</div>
                  </header>
                  <div className="rsd-question-result-content">
                    <div className={`rsd-question-charts-row ${isMulti ? 'rsd-row-multi' : ''}`}>
                      <div className="rsd-question-pie-section">
                        {isMulti ? (
                          <>
                            <div className="rsd-multiple-choice-label">{t('research.multipleAnswers')}</div>
                            <HorizontalBarChart data={question.answers.map((a, i) => ({ label: a.answer_text, percentage: a.percentage, color: ANSWER_COLORS[i % ANSWER_COLORS.length] }))} />
                          </>
                        ) : (
                          <>
                            <div className="rsd-question-pie-wrapper">
                              <Donut data={question.answers.map((a, i) => ({ value: a.count, color: ANSWER_COLORS[i % ANSWER_COLORS.length] }))} totalLabel={t('research.answersTotal')} size={200} />
                            </div>
                            <div className="rsd-question-legend">
                              {question.answers.map((a, i) => (
                                <div key={a.id} className="rsd-question-legend-item">
                                  <span className="rsd-legend-color" style={{ background: ANSWER_COLORS[i % ANSWER_COLORS.length] }} />
                                  {isCreative && a.answer_image
                                    ? <img className="rsd-legend-thumb" src={imgUrl(a.answer_image)} alt={`Option ${i + 1}`} />
                                    : <span className="rsd-legend-text">{a.answer_text}</span>}
                                  <span className="rsd-legend-value">{a.count.toLocaleString(currentLocale())} · {a.percentage}%</span>
                                </div>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      {question.demographic_breakdown.length > 0 && (
                        <div className="rsd-question-breakdown-section">
                          <h5 className="rsd-breakdown-title">{t('research.breakdownDemographics')}</h5>
                          <StackedBarChart breakdown={question.demographic_breakdown} answers={question.answers} />
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <div className="rsd-share-actions">
          <button className="rsd-btn rsd-btn-secondary" onClick={() => window.print()}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z" /></svg>
            {t('research.print')}
          </button>
        </div>

      </div>
    </div>
  );
};

export default ResearchShareDievo;
